import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { AiAbstractChatComponent } from '../../ai-abstract-chat/ai-abstract-chat.component';
import { AIChatGeneratorService } from '../../../services/ai-generator.service';
import { AIPromptService } from '../../../services/ai-prompt.service';
import { AiErrorHandlerService } from '../../../services/ai-error-handler.service';
import { ProjectChannelMessage, ProjectModelWithRelations } from '@rappider/rappider-sdk';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectActiveProjectId } from 'libs/project/src/lib/states/active-project-state/active-project.selectors';
import { selectActivePerson } from 'libs/authentication/src/lib/state/authentication.selectors';
import { ActivatedRoute } from '@angular/router';
import { aiFunctionIds as AI_FUNCTION_IDS, AIGenerationType } from '../../../utils/ai-response-types.interface';
import { MessageType } from '@rappider/rappider-components/utils';
import { getProjectModelsWithFields } from '@rappider/shared';

@Component({
  selector: 'rappider-ai-generate-data-model',
  standalone: true,
  imports: [
    CommonModule,
    AiAbstractChatComponent
  ],
  templateUrl: './ai-generate-data-model.component.html',
  styleUrls: ['./ai-generate-data-model.component.scss'],
})
export class AiGenerateDataModelComponent implements OnInit, OnDestroy {

  @Input() projectId?: string;

  @ViewChild('abstractChat') abstractChat!: AiAbstractChatComponent & { messages: ProjectChannelMessage[] };

  activeProjectId?: string;
  activePersonId?: string;
  selectedProjectModelId?: string;
  aiFunctionIds = AI_FUNCTION_IDS;
  projectModels: ProjectModelWithRelations[] | undefined;

  // Edit mode properties
  isEditMode = false;
  editingModel?: ProjectModelWithRelations;

  // Page configuration based on mode
  pageTitle = 'Manage Data Models With AI';
  pageDescription = 'Describe the data structure you need for your application. AI will help you create and update data models with proper fields, types, and relationships.';
  inputPlaceholder = 'Describe the data model you want to create or update (e.g., \'Create a User model with name, email, phone fields and relation to Profile\')';
  buttonText = 'Generate Data Models';

  get examplePrompts(): string[] {
    if (this.isEditMode) {
      return [
        'Add a new field: ExampleField',
        'Delete a field: ...',
        'Update a field: ...',
        'Create a relation: ExampleDataModel'
      ];
    } else {
      const models = this.projectModels?.slice(3, 5) || [];
      const modelName1 = models[0]?.name || 'Data';
      const modelName2 = models[1]?.name || 'Data';

        return [
          'Generate a fully structured and production-ready Product data model, including fields such as name (string, required), ' +
          'price (decimal, required), and category (relation), with appropriate data types, validations, constraints, ' +
          'and relationships to other relevant models, ensuring scalability and maintainability...',
          `Add a new field to the ${modelName1} model: ExampleField`,
          `Delete a field from the ${modelName2} model: ...`,
          `Create a relation between ${modelName1} and ${modelName2} models`
        ];
    }
  }

  get aiStartConfig() {
    return {
      title: this.pageTitle,
      description: this.pageDescription,
      placeholder: this.inputPlaceholder,
      buttonText: this.buttonText,
      saveButtonIcon: 'fa-solid fa-database',
      type: AIGenerationType.DATA_MODEL,
      imageUpload: true,
      redirectUrl: '/ai/generate-data-model'
    };
  }

  subscriptions: Subscription[] = [];

  private destroy$ = new Subject<void>();
  private loadingTimeout: NodeJS.Timeout | null = null;
  private readonly AI_SENDER_ID = '00000000-0000-0000-0000-000000000001';

  constructor(
    private aiChatGeneratorService: AIChatGeneratorService,
    private aiPromptService: AIPromptService,
    private aiErrorHandlerService: AiErrorHandlerService,
    private store: Store,
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {
    // Constructor intentionally left empty
  }

  ngOnInit(): void {
    this.checkEditMode();
    this.subscriptions = [
      this.subscribeToActiveProject(),
      this.subscribeToActivePerson(),
      this.subscribeToProjectModels(),
      ...this.subscribeToAIActions(),
      this.subscribeToModelUpdateSuccess()
    ];

  }

  ngOnDestroy(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    this.subscriptions.forEach(subscription => subscription.unsubscribe());

    this.destroy$.next();
    this.destroy$.complete();
  }


  onResponseAction(event: { type: string; data: unknown }): void {
    console.log('Response action received:', event);
  }

  public onMessageSent(event: { message: string; files?: File[] }): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }

    this.loadingTimeout = setTimeout(() => {
      this.abstractChat.isTyping = true;
    }, 500);

    const contextualPrompt = this.aiPromptService.buildDataModelContextualPrompt(
      event.message,
      this.abstractChat.messages,
      this.projectModels,
      this.editingModel
    );
    this.generateDataModel(contextualPrompt);
  }


  private generateDataModel(prompt: string): void {
    const projectId = this.projectId || this.activeProjectId;
    if (!projectId) {
      return;
    }

    this.aiChatGeneratorService.processAIGenerationRequest({
      prompt: prompt,
      type: AIGenerationType.DATA_MODEL,
      projectId: projectId
    });
  }


  private subscribeToActiveProject() {
    return this.store.select(selectActiveProjectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(projectId => {
        this.activeProjectId = projectId;
      });
  }

  private subscribeToProjectModels() {
    return this.store.select(getProjectModelsWithFields).subscribe((projectModels => {
      this.projectModels = projectModels as ProjectModelWithRelations[];
      if (this.isEditMode && !this.editingModel && this.projectModels) {
        this.route.params.subscribe(params => {
          if (params['modelName']) {
            this.findEditingModel(params['modelName']);
            if (this.editingModel) {
              this.selectedProjectModelId = this.editingModel.id;
              this.updatePageConfigurationTexts();
            } else {
              // Model not found, but still update with the name from URL
              console.warn(`Model not found for name: ${params['modelName']}`);
              this.updatePageConfigurationTexts();
            }
          }
        });
      }
    }));
  }

  private subscribeToActivePerson() {
    return this.store.select(selectActivePerson)
      .pipe(takeUntil(this.destroy$))
      .subscribe(activePerson => {
        this.activePersonId = activePerson?.id;
      });
  }

  private subscribeToAIActions() {
    return [
      this.aiChatGeneratorService.subscribeToAIGenerationSuccess()
        .pipe(takeUntil(this.destroy$))
        .subscribe((action: unknown) => {
          if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
            this.loadingTimeout = null;
          }
          if (this.abstractChat) {
            this.abstractChat.clearLoadingState();
          }
          const actionData = action as Record<string, unknown>;
          this.addAIResponseToChat(actionData.payload);
        }),

      this.aiChatGeneratorService.subscribeToAIGenerationError()
        .pipe(takeUntil(this.destroy$))
        .subscribe((action: unknown) => {
          if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
            this.loadingTimeout = null;
          }
          if (this.abstractChat) {
            this.abstractChat.clearLoadingState();
          }
          const actionData = action as Record<string, unknown>;
          const payload = actionData.payload as Record<string, unknown> | undefined;
          const error = payload?.error;
          if (error) {
            const errorMessage = this.aiErrorHandlerService.extractErrorMessage(error);
            this.addErrorMessageToChat(errorMessage);
          }
        }),

      this.aiChatGeneratorService.subscribeToSaveSuccess()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.handleSaveSuccess();
        })
    ];
  }

  private addAIResponseToChat(responseData: unknown): void {
    const dataObj = responseData as Record<string, unknown>;
    const actions = dataObj.actions || (dataObj.generated as Record<string, unknown>)?.actions;
    const hasActions = !!(actions && Array.isArray(actions) && actions.length > 0);
    const isEmptyResponse = !this.aiChatGeneratorService.validateResponseData(responseData);

    if (hasActions) {
      this.abstractChat.isTyping = true;
    }

    const aiMessage: ProjectChannelMessage = {
      id: Date.now().toString(),
      message: isEmptyResponse ? this.aiChatGeneratorService.generateErrorMessage() : this.generateDataModelMessage(responseData),
      date: new Date().toISOString(),
      channelId: 'ai-channel',
      projectId: this.activeProjectId || 'unknown-project',
      senderId: this.AI_SENDER_ID,
      status: 'new',
      responseData: isEmptyResponse ? null : responseData,
      responseType: AIGenerationType.DATA_MODEL,
      isEmptyResponse: isEmptyResponse,
      type: MessageType.AI,
    };

    this.abstractChat?.addAIResponse(aiMessage);
  }

  /**
   * Handle save success to update selected model ID
   * Gets the most recently created model from the store
   */
  private handleSaveSuccess(): void {
    this.store.select((state: unknown) => {
      const stateObj = state as Record<string, unknown>;
      const projectModel = stateObj.projectModel as Record<string, unknown>;
      return projectModel?.data as ProjectModelWithRelations[];
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((projectModels: ProjectModelWithRelations[]) => {
        if (projectModels && projectModels.length > 0) {
          const latestModel = [...projectModels]
            .sort((a: ProjectModelWithRelations, b: ProjectModelWithRelations) =>
              new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime())[0];

          this.selectedProjectModelId = latestModel.id;
          this.cd.detectChanges();
        }
      });
  }

  /**
   * Handle update success to update selected model ID
   */
  private handleUpdateSuccess(projectModelId: string): void {
    this.selectedProjectModelId = projectModelId;
    this.cd.detectChanges();
  }

  /**
   * Subscribe to model update success events
   */
  private subscribeToModelUpdateSuccess(): Subscription {
    const handleModelUpdateSuccess = (event: CustomEvent) => {
      const projectModelId = event.detail?.projectModelId;
      if (projectModelId) {
        this.handleUpdateSuccess(projectModelId);
      }
    };

    window.addEventListener('modelUpdateSuccess', handleModelUpdateSuccess as EventListener);

    return new Subscription(() => {
      window.removeEventListener('modelUpdateSuccess', handleModelUpdateSuccess as EventListener);
    });
  }

  /**
   * Generates appropriate message text for data model responses
   * @param responseData The response data containing data models
   * @returns Generated message text
   */
  private generateDataModelMessage(responseData: unknown): string {
    if (!responseData) {
      return 'Data models have been generated successfully';
    }

    if (typeof responseData === 'object' && responseData !== null) {
      const dataObj = responseData as Record<string, unknown>;
      const generated = dataObj.generated as Record<string, unknown>;

      if (generated?.summary && typeof generated.summary === 'string') {
        return generated.summary;
      }
    }

    return 'Data models have been generated successfully';
  }

  /**
   * Check if component is in edit mode based on route parameters
   */
  private checkEditMode(): void {
    this.route.params.subscribe(params => {
      if (params['modelName']) {
        this.isEditMode = true;
        this.findEditingModel(params['modelName']);
        this.updatePageConfigurationForEditMode();
      }
    });
  }

  /**
   * Find editing model by name
   */
  private findEditingModel(modelName: string): void {
    if (this.projectModels) {
      this.editingModel = this.projectModels.find(model =>
        model.name === modelName || model.dataTableName === modelName
      );
    }
  }

  /**
   * Update page configuration for edit mode
   */
  private updatePageConfigurationForEditMode(): void {
    if (!this.editingModel && this.projectModels) {
      this.route.params.subscribe(params => {
        if (params['modelName']) {
          this.findEditingModel(params['modelName']);
          this.updatePageConfigurationTexts();
        }
      });
    } else {
      this.updatePageConfigurationTexts();
    }
  }

  /**
   * Update page configuration texts with model name
   */
  private updatePageConfigurationTexts(): void {
    const modelName = this.editingModel?.name || 'Data Model';
    this.pageTitle = `Update ${modelName} Data Model With AI`;
    this.pageDescription = `Describe the changes you want to make to your ${modelName} data model. AI will help you update the model with new fields, types, and relationships.`;
    this.inputPlaceholder = `Describe the changes you want to make to the ${modelName} model (e.g., 'Add a phone field, update email to be required, add relation to Address model')`;
    this.buttonText = 'Update Data Model';
    this.cd.detectChanges();
  }

  /**
   * Add error message to chat interface
   * @param message - Error message to display
   */
  private addErrorMessageToChat(message: string): void {
    if (!this.abstractChat) {
      return;
    }

    const aiMessage: ProjectChannelMessage = {
      id: Date.now().toString(),
      message: message,
      date: new Date().toISOString(),
      channelId: 'ai-channel',
      projectId: this.activeProjectId || 'unknown-project',
      senderId: this.AI_SENDER_ID,
      status: 'new',
      responseData: null,
      responseType: AIGenerationType.DATA_MODEL,
      isEmptyResponse: false,
      type: MessageType.AI,
    };

    this.abstractChat.addAIResponse(aiMessage);
  }

}
